import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isBrowserSupported, useProctoring } from 'proctoring-sdk/index'
import { ProctoringPermissionsGate } from './ProctoringPermissionsGate'
import { ProctoringBlockerModal } from './ProctoringBlockerModal'
import { ProctoringExitConfirmModal } from './ProctoringExitConfirmModal'
import { createProctoringSentryObservability } from './proctoringSentryObservability'
import type { MonitorKeyPayload } from 'Utils/monitorKey.utils'
import type { ProctoredShellRenderProps } from 'Components/Proctoring/proctoring.types'
import { errorProctoring, logProctoring, warnProctoring } from 'proctoring-sdk/debugLog'
import { clearProctoringTimerSnapshot } from 'Utils/proctoringTimer.utils'

export const ProctoredTaskShell = ({
    payload,
    children,
    onExitRequested,
}: {
    payload: MonitorKeyPayload
    children: (props: ProctoredShellRenderProps) => ReactNode
    onExitRequested?: () => void
}) => {
    const [permissionsGranted, setPermissionsGranted] = useState(false)
    const [cycleId, setCycleId] = useState(0)
    const [exitConfirmOpen, setExitConfirmOpen] = useState(false)

    const browserSupported = useMemo(() => {
        try {
            return isBrowserSupported()
        } catch {
            return false
        }
    }, [])

    const referenceId = String(payload.reference_id)
    const observability = useMemo(
        () =>
            createProctoringSentryObservability({
                tenant: payload.tenant,
                referenceId,
                profile: payload.profile,
            }),
        [payload.tenant, referenceId, payload.profile]
    )

    const proctoring = useProctoring({
        tenant: payload.tenant,
        referenceId,
        profile: payload.profile,
        expectedDurationSeconds: payload.duration,
        autoStart: false,
        observability,
    })

    const proctoringRef = useRef(proctoring)
    proctoringRef.current = proctoring

    const sessionActiveRef = useRef(false)
    const mediaInterruptedRef = useRef(false)

    const handlePermissionsGranted = () => {
        logProctoring('ui:continue-after-permissions', {
            status: proctoringRef.current.status,
            mediaInterrupted: mediaInterruptedRef.current,
        })
        setPermissionsGranted(true)

        void proctoringRef.current.start().catch(err => {
            errorProctoring('failed to start after continue', err)
        })
    }

    useEffect(() => {
        const unsubs = [
            proctoring.events.on('sessionCreated', result => {
                sessionActiveRef.current = true
                mediaInterruptedRef.current = false
                logProctoring('ui:sessionCreated', result)
            }),
            proctoring.events.on('streamDisconnected', reason => {
                const safeReason = reason || 'SCREEN_SHARING_STOPPED'
                warnProctoring(
                    'stream disconnected — exam continues; flags raised on proctoring side',
                    safeReason
                )
                mediaInterruptedRef.current = true
                sessionActiveRef.current = false
            }),
        ]
        return () => unsubs.forEach(unsub => unsub())
    }, [proctoring.events])

    const endProctoringSession = useCallback(async () => {
        if (mediaInterruptedRef.current) {
            logProctoring('ui:endSession:skip-media-interrupted')
            sessionActiveRef.current = false
            try {
                await proctoringRef.current.destroy()
            } catch (e) {
                errorProctoring('destroy after interrupt failed', e)
            }
            return
        }
        if (!sessionActiveRef.current) {
            logProctoring('ui:endSession:skip-no-session')
            return
        }
        sessionActiveRef.current = false
        logProctoring('ui:endSession')
        const current = proctoringRef.current
        try {
            await current.stop()
        } catch (e) {
            errorProctoring('stop failed', e)
        }
        try {
            await current.destroy()
        } catch (e) {
            errorProctoring('destroy failed', e)
        }
    }, [])

    useEffect(() => {
        const onPageHide = () => {
            mediaInterruptedRef.current = true
            sessionActiveRef.current = false
        }
        window.addEventListener('pagehide', onPageHide)
        return () => {
            window.removeEventListener('pagehide', onPageHide)
            void endProctoringSession()
        }
    }, [endProctoringSession])

    const handleExitConfirmed = () => {
        setExitConfirmOpen(false)
        onExitRequested?.()
    }

    const beginNextTaskCycle = useCallback(async () => {
        logProctoring('ui:nextTaskCycle')
        clearProctoringTimerSnapshot(referenceId)
        mediaInterruptedRef.current = false
        setPermissionsGranted(false)
        setCycleId(id => id + 1)
        await endProctoringSession()
        try {
            await proctoringRef.current.initialize()
        } catch (e) {
            errorProctoring('re-init on next-task failed', e)
        }
    }, [endProctoringSession, referenceId])

    return (
        <>
            {!permissionsGranted ? (
                <ProctoringPermissionsGate
                    key={cycleId}
                    proctoring={proctoring}
                    browserSupported={browserSupported}
                    onPermissionsGranted={handlePermissionsGranted}
                    profile={payload.profile}
                />
            ) : null}
            <div hidden={!permissionsGranted}>
                {children({
                    proctoring,
                    permissionsGranted,
                    beginNextTaskCycle,
                    endProctoringSession,
                })}
            </div>
            {permissionsGranted && proctoring.isActive ? (
                <ProctoringBlockerModal
                    blocker={proctoring.blocker}
                    checking={proctoring.blockerChecking}
                    lastFailureReason={proctoring.blockerFailureReason}
                    deniedDevices={proctoring.blockerDeniedDevices}
                    exitLabel="Task"
                    onCheckAgain={() => void proctoring.checkAgainBlocker()}
                    onExitRequested={() => setExitConfirmOpen(true)}
                />
            ) : null}
            <ProctoringExitConfirmModal
                open={exitConfirmOpen}
                exitLabel="Task"
                onCancel={() => setExitConfirmOpen(false)}
                onConfirm={handleExitConfirmed}
            />
        </>
    )
}
